<script setup lang="ts">
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { Entity, EntityCreateDto } from "~/api";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  entityDefinitionGuid: string;
  suggestedName?: string;
}>();

const emit = defineEmits<{
  (e: "add:entity", value: Entity): void;
}>();

const myInfoStore = useMyInfoStore();
const entityDefinitionStore = useEntityDefinitionStore();

const form = ref({
  entityName: props.suggestedName ?? "",
  status: "Default",
});

const dialogIsOpen = defineModel<boolean>({ required: true });

const entityName = computed(
  () =>
    entityDefinitionStore.nameOfType(props.entityDefinitionGuid) ?? "Entity",
);

const createEntity = async () => {
  if (!props.entityDefinitionGuid) {
    showError("Please select a entity type to create an entity");
    return;
  }

  if (!form.value.entityName) {
    showError("Please enter a name for the " + entityName.value);
    return;
  }

  const entityToCreate: EntityCreateDto = {
    sysOrgGuid: myInfoStore.currentSysOrgGuid,
    name: form.value.entityName,
    status: form.value.status,
    entityDefinitionGuid: props.entityDefinitionGuid,
  };

  await apiClient.entity
    .createEntity(entityToCreate)
    .then((res) => {
      ElMessage.success("Entity created successfully");
      dialogIsOpen.value = false;
      emit("add:entity", res);
    })
    .catch(showError);
};

const handleCloseDialog = () => {
  dialogIsOpen.value = false;
};

onMounted(async () => {
  await entityDefinitionStore.load();
});
</script>
<template>
  <el-dialog
    v-model="dialogIsOpen"
    :width="600"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleCloseDialog"
  >
    <template #header>Create a {{ entityName }}</template>
    <div class="EntityInstanceCreator">
      <el-form
        v-model="form"
        class="create-entity-inputs"
        label-width="auto"
        label-position="left"
      >
        <!-- <el-form-item v-if="!props.userGuid" label="For">
          <el-select
            v-model="form.userGuid"
            placeholder="Select a user"
            clearable
            filterable
          >
            <el-option
              v-for="user in loginAccountStore.List"
              :key="user.userGuid"
              :label="user.loginName || user.email || user.userGuid"
              :value="user.userGuid"
            ></el-option>
          </el-select>
        </el-form-item> -->
        <el-form-item
          :label="'Name for ' + entityName"
          style="font-size: 14px; font-family: poppins, sans-serif"
        >
          <el-input
            v-model="form.entityName"
            :placeholder="`${entityName} Name`"
          ></el-input>
        </el-form-item>
        <el-form-item class="create-button">
          <el-button
            type="primary"
            :disabled="!props.entityDefinitionGuid || !form.entityName"
            @click="createEntity"
            >Create</el-button
          >
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>
<style lang="less">
.EntityInstanceCreator {
  .create-button {
    margin-top: 1em;
    .el-form-item__content {
      justify-content: end;
    }
  }
  .el-input__inner {
    font-size: 14px;
    font-family: poppins, sans-serif;
  }
}
</style>
